import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowDownward,
    ChevronLeft,
    ChevronRight,
    DragIndicator,
    Inventory2Outlined,
    OpenInNew,
    PushPin,
    PushPinOutlined,
    Undo,
    ViewQuilt,
} from "@mui/icons-material";
import { Category, Product } from "../../types";
import { productService } from "../../services/productService";
import {
    ArrangePreview,
    ArrangedProduct,
    merchandisingService,
} from "../../services/merchandisingService";
import { ProductSearchAutocomplete } from "../../components/common/ProductSearchAutocomplete";
import { getProductThumbnailUrl } from "../../components/common/ProductThumbnail";
import { formatCurrencyBDT } from "../../utils/currency";
import { shopPageUrl } from "../../config/storefront";

/**
 * Arrange Shop — what shoppers see first on the website.
 *
 * The website's default ("Recommended") order is automatic: best sellers
 * and new arrivals rise, products without a photo sink, out-of-stock ones
 * always go last. On top of that, staff pin products to the top of the
 * whole shop or of one category, and can push a product down everywhere.
 * Changes stay on this page until Save.
 */

interface ScopeOption {
    id: string | null; // null = whole shop
    label: string;
    slug?: string;
}

const WHOLE_SHOP: ScopeOption = { id: null, label: "Whole shop" };

const price = (n: number | undefined) => formatCurrencyBDT(Number(n ?? 0), { ignoreDecimals: true });

/** "Cat › Food › Dry" from each category's parent chain. */
function categoryOptions(categories: Category[]): ScopeOption[] {
    const byId = new Map(categories.map((c) => [c.category_id, c]));
    const label = (c: Category): string => {
        const names = [c.name];
        let parent = c.parent_id ? byId.get(c.parent_id) : undefined;
        for (let i = 0; parent && i < 6; i++) {
            names.unshift(parent.name);
            parent = parent.parent_id ? byId.get(parent.parent_id) : undefined;
        }
        return names.join(" › ");
    };
    return categories
        .map((c) => ({ id: c.category_id, label: label(c), slug: c.slug }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

/** Where an unpinned product falls back to: among in-stock products by score. */
function insertByScore(list: ArrangedProduct[], item: ArrangedProduct): ArrangedProduct[] {
    const rank = (a: ArrangedProduct) => (a.in_stock ? 1 : 0) * 1e6 + a.score;
    const at = list.findIndex((x) => rank(x) < rank(item));
    return at === -1 ? [...list, item] : [...list.slice(0, at), item, ...list.slice(at)];
}

/** A product picked from search, shaped like the rows the API returns. */
function fromSearch(product: Product): ArrangedProduct {
    const available = (product.stock?.quantity ?? 0) - (product.stock?.reserved_quantity ?? 0);
    return {
        product,
        units_sold: 0,
        is_new: false,
        has_photo: !!product.images?.length,
        in_stock: available > 0,
        score: 0,
        pin_position: null,
    };
}

// ─── Product card ────────────────────────────────────────────────────────────

interface CardProps {
    item: ArrangedProduct;
    position?: number;
    salesDays: number;
    faded?: boolean;
    note?: string;
    children?: React.ReactNode; // actions
    handle?: React.ReactNode;
}

const ProductCard: React.FC<CardProps> = ({ item, position, salesDays, faded, note, children, handle }) => {
    const p = item.product;
    const photo = getProductThumbnailUrl(p.images);
    const onSale = p.is_on_sale && p.effective_price != null && p.effective_price < p.selling_price;
    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                opacity: faded ? 0.55 : 1,
                transition: "opacity .15s",
            }}
        >
            <Box sx={{ position: "relative", aspectRatio: "1 / 1", bgcolor: "grey.50" }}>
                {photo ? (
                    <Box
                        component="img"
                        src={photo}
                        alt=""
                        loading="lazy"
                        sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                    />
                ) : (
                    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "grey.400" }}>
                        <Inventory2Outlined fontSize="large" />
                    </Box>
                )}
                {position != null && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            minWidth: 24,
                            height: 24,
                            px: 0.75,
                            borderRadius: 12,
                            bgcolor: "rgba(0,0,0,.65)",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {position}
                    </Box>
                )}
                {handle && <Box sx={{ position: "absolute", top: 4, right: 4 }}>{handle}</Box>}
                {!item.in_stock && (
                    <Chip
                        label="Out of stock"
                        size="small"
                        color="error"
                        sx={{ position: "absolute", bottom: 6, left: 6, fontWeight: 600 }}
                    />
                )}
            </Box>
            <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 0.75, flex: 1 }}>
                <Typography
                    variant="body2"
                    fontWeight={600}
                    title={p.name}
                    sx={{
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "2.6em",
                    }}
                >
                    {p.name}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {onSale ? (
                        <>
                            <Box component="span" sx={{ fontWeight: 700, color: "error.main" }}>
                                {price(p.effective_price)}
                            </Box>{" "}
                            <Box component="span" sx={{ color: "text.secondary", textDecoration: "line-through", fontSize: 12 }}>
                                {price(p.selling_price)}
                            </Box>
                        </>
                    ) : (
                        <Box component="span" sx={{ fontWeight: 700 }}>{price(p.selling_price)}</Box>
                    )}
                </Typography>
                <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5}>
                    {item.units_sold > 0 && (
                        <Tooltip title={`Units sold in the last ${salesDays} days, all channels`}>
                            <Chip size="small" color="success" variant="outlined" label={`${item.units_sold} sold`} />
                        </Tooltip>
                    )}
                    {item.is_new && <Chip size="small" color="info" variant="outlined" label="New" />}
                    {p.is_featured && <Chip size="small" color="primary" variant="outlined" label="Featured" />}
                    {onSale && <Chip size="small" color="error" variant="outlined" label="On sale" />}
                    {!item.has_photo && (
                        <Tooltip title="Products without a photo are shown lower">
                            <Chip size="small" color="warning" variant="outlined" label="No photo" />
                        </Tooltip>
                    )}
                    {!p.is_visible && <Chip size="small" variant="outlined" label="Hidden" />}
                    {note && <Chip size="small" label={note} />}
                </Stack>
                {children && <Box sx={{ mt: "auto", pt: 0.5 }}>{children}</Box>}
            </Box>
        </Paper>
    );
};

// ─── Pinned card (sortable) ──────────────────────────────────────────────────

const SortablePinned: React.FC<{
    item: ArrangedProduct;
    index: number;
    count: number;
    salesDays: number;
    onMove: (from: number, to: number) => void;
    onUnpin: () => void;
}> = ({ item, index, count, salesDays, onMove, onUnpin }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: item.product.product_id,
    });
    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                zIndex: isDragging ? 2 : undefined,
                position: "relative",
                boxShadow: isDragging ? 6 : 0,
                borderRadius: 2,
            }}
        >
            <ProductCard
                item={item}
                position={index + 1}
                salesDays={salesDays}
                note={!item.in_stock ? "Shown last until restocked" : undefined}
                handle={
                    <IconButton
                        ref={setActivatorNodeRef}
                        size="small"
                        aria-label={`Drag ${item.product.name}`}
                        sx={{ bgcolor: "rgba(255,255,255,.9)", "&:hover": { bgcolor: "#fff" }, cursor: "grab", touchAction: "none" }}
                        {...attributes}
                        {...listeners}
                    >
                        <DragIndicator fontSize="small" />
                    </IconButton>
                }
            >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip title="Move earlier">
                        <span>
                            <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label="Move earlier">
                                <ChevronLeft fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Move later">
                        <span>
                            <IconButton
                                size="small"
                                disabled={index === count - 1}
                                onClick={() => onMove(index, index + 1)}
                                aria-label="Move later"
                            >
                                <ChevronRight fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" color="inherit" startIcon={<PushPin fontSize="small" />} onClick={onUnpin}>
                        Unpin
                    </Button>
                </Stack>
            </ProductCard>
        </Box>
    );
};

const gridSx = {
    display: "grid",
    gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))",
        sm: "repeat(3, minmax(0, 1fr))",
        md: "repeat(4, minmax(0, 1fr))",
        lg: "repeat(5, minmax(0, 1fr))",
        xl: "repeat(6, minmax(0, 1fr))",
    },
    gap: 1.5,
} as const;

// ─── Page ────────────────────────────────────────────────────────────────────

export const ArrangeShop: React.FC = () => {
    const [scopes, setScopes] = useState<ScopeOption[]>([WHOLE_SHOP]);
    const [scope, setScope] = useState<ScopeOption>(WHOLE_SHOP);
    const [pendingScope, setPendingScope] = useState<ScopeOption | null>(null);

    const [preview, setPreview] = useState<ArrangePreview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState("");
    const [saving, setSaving] = useState(false);

    // Local edits, applied on Save.
    const [pins, setPins] = useState<ArrangedProduct[]>([]);
    const [unpinned, setUnpinned] = useState<ArrangedProduct[]>([]);
    const [pushChanges, setPushChanges] = useState<Map<string, boolean>>(new Map());
    const [searchPick, setSearchPick] = useState<Product | null>(null);
    // Remounts the search box after a pick, so it's empty for the next one.
    const [searchKey, setSearchKey] = useState(0);

    useEffect(() => {
        productService
            .getCategories({ limit: 1000, active_only: true })
            .then((cats) => setScopes([WHOLE_SHOP, ...categoryOptions(cats)]))
            .catch(() => setScopes([WHOLE_SHOP]));
    }, []);

    const load = useCallback(async (target: ScopeOption) => {
        setLoading(true);
        setError("");
        try {
            const data = await merchandisingService.getPreview(target.id);
            setPreview(data);
            setPins(data.pins);
            setUnpinned([]);
            setPushChanges(new Map());
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't load the shop's order. Try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(scope);
    }, [load, scope]);

    const pinsChanged = useMemo(() => {
        const before = (preview?.pins ?? []).map((p) => p.product.product_id).join(",");
        return before !== pins.map((p) => p.product.product_id).join(",");
    }, [preview, pins]);
    const dirty = pinsChanged || pushChanges.size > 0;

    // Warn before leaving the page with unsaved changes.
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const pinnedIds = useMemo(() => new Set(pins.map((p) => p.product.product_id)), [pins]);

    // The automatic order as it will be: minus anything pinned here, plus
    // products unpinned here at the place their score earns them.
    const automatic = useMemo(() => {
        let list = (preview?.automatic ?? []).filter((a) => !pinnedIds.has(a.product.product_id));
        for (const u of unpinned) {
            if (!pinnedIds.has(u.product.product_id)) list = insertByScore(list, u);
        }
        return list;
    }, [preview, pinnedIds, unpinned]);

    const isPushed = (item: ArrangedProduct) =>
        pushChanges.get(item.product.product_id) ?? !!item.product.is_pushed_down;

    const togglePush = (item: ArrangedProduct) => {
        const id = item.product.product_id;
        const original = !!item.product.is_pushed_down;
        setPushChanges((prev) => {
            const next = new Map(prev);
            const now = next.get(id) ?? original;
            if (!now === original) next.delete(id);
            else next.set(id, !now);
            return next;
        });
    };

    const maxPins = preview?.max_pins ?? 50;
    const pin = (item: ArrangedProduct) => {
        setSaved("");
        if (pinnedIds.has(item.product.product_id)) return;
        if (pins.length >= maxPins) {
            setError(`Up to ${maxPins} pinned products per list — unpin one first.`);
            return;
        }
        setPins((prev) => [...prev, item]);
        // A pinned product isn't pushed down.
        if (isPushed(item)) togglePush(item);
    };

    const unpin = (item: ArrangedProduct) => {
        setSaved("");
        setPins((prev) => prev.filter((p) => p.product.product_id !== item.product.product_id));
        if (!(preview?.automatic ?? []).some((a) => a.product.product_id === item.product.product_id)) {
            setUnpinned((prev) => [...prev, item]);
        }
    };

    const move = (from: number, to: number) => {
        setSaved("");
        setPins((prev) => arrayMove(prev, from, to));
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const onDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const from = pins.findIndex((p) => p.product.product_id === active.id);
        const to = pins.findIndex((p) => p.product.product_id === over.id);
        if (from >= 0 && to >= 0) move(from, to);
    };

    const discard = () => {
        if (!preview) return;
        setPins(preview.pins);
        setUnpinned([]);
        setPushChanges(new Map());
        setError("");
    };

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const push = Array.from(pushChanges).filter(([, v]) => v).map(([id]) => id);
            const undo = Array.from(pushChanges).filter(([, v]) => !v).map(([id]) => id);
            if (pinsChanged) {
                await merchandisingService.savePins(scope.id, pins.map((p) => p.product.product_id));
            }
            await merchandisingService.setPushedDown(push, true);
            await merchandisingService.setPushedDown(undo, false);
            await load(scope);
            setSaved("Saved. The website shows the new order within a minute.");
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't save. Your changes are still here — try again.");
        } finally {
            setSaving(false);
        }
    };

    const changeScope = (next: ScopeOption | null) => {
        if (!next || next.id === scope.id) return;
        if (dirty) setPendingScope(next);
        else {
            setSaved("");
            setScope(next);
        }
    };

    const addFromSearch = (product: Product | null) => {
        setSearchPick(null);
        setSearchKey((k) => k + 1);
        if (!product) return;
        const known =
            automatic.find((a) => a.product.product_id === product.product_id) ||
            preview?.pushed_down.find((a) => a.product.product_id === product.product_id);
        pin(known ?? fromSearch(product));
    };

    const salesDays = preview?.sales_days ?? 60;
    const newDays = preview?.new_days ?? 30;
    const pushedList = (preview?.pushed_down ?? []).filter((p) => !pinnedIds.has(p.product.product_id));
    const scopeName = scope.id ? scope.label.split(" › ").pop() : "the shop";
    // Products in the automatic order: everything here minus the pinned and
    // pushed-down ones (the preview shows the first part of it).
    const automaticTotal = Math.max(
        automatic.length,
        (preview?.automatic_total ?? 0) - pins.length - pushedList.length,
    );

    return (
        <Box sx={{ pb: dirty ? 10 : 0 }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                <Box sx={{ minWidth: 0, maxWidth: 760 }}>
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" }, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <ViewQuilt color="primary" /> Arrange Shop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose what shoppers see first. Pinned products come first, in your order. The rest arrange
                        themselves: best sellers of the last {salesDays} days, new arrivals (first {newDays} days),
                        featured and sale products rise; products without a photo sink; out-of-stock products always
                        go last.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<OpenInNew />}
                    href={shopPageUrl(scope.slug)}
                    target="_blank"
                    rel="noopener"
                >
                    View on website
                </Button>
            </Box>

            {/* Scope + search */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, borderRadius: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 1.5 }}>
                    <Autocomplete
                        options={scopes}
                        value={scope}
                        onChange={(_, v) => changeScope(v)}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={(o) => o.label}
                        disableClearable
                        renderInput={(params) => (
                            <TextField {...params} size="small" label="Arranging" helperText="The whole shop, or one category's page" />
                        )}
                    />
                    <ProductSearchAutocomplete
                        key={searchKey}
                        value={searchPick}
                        onChange={(p) => addFromSearch(p as Product | null)}
                        label="Find a product to pin"
                        helperText="Search by name or SKU — it's added to the end of the pinned products"
                        showStock
                    />
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}
            {saved && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved("")}>
                    {saved}
                </Alert>
            )}

            {loading && !preview ? (
                <Box sx={gridSx}>
                    {Array.from({ length: 10 }).map((_, i) => (
                        <Skeleton key={i} variant="rounded" sx={{ aspectRatio: "3 / 4", height: "auto" }} />
                    ))}
                </Box>
            ) : (
                <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s" }}>
                    {/* Pinned */}
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <PushPin fontSize="small" color="primary" /> Pinned to the top of {scopeName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {pins.length} of {maxPins} · drag to reorder
                        </Typography>
                    </Box>
                    {pins.length === 0 ? (
                        <Paper
                            variant="outlined"
                            sx={{ p: 3, mb: 3, borderRadius: 2, borderStyle: "dashed", textAlign: "center", color: "text.secondary" }}
                        >
                            <Typography variant="body2">
                                Nothing pinned yet. Use <b>Pin</b> on a product below, or find one with the search above.
                            </Typography>
                        </Paper>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                            <SortableContext items={pins.map((p) => p.product.product_id)} strategy={rectSortingStrategy}>
                                <Box sx={{ ...gridSx, mb: 3 }}>
                                    {pins.map((item, i) => (
                                        <SortablePinned
                                            key={item.product.product_id}
                                            item={item}
                                            index={i}
                                            count={pins.length}
                                            salesDays={salesDays}
                                            onMove={move}
                                            onUnpin={() => unpin(item)}
                                        />
                                    ))}
                                </Box>
                            </SortableContext>
                        </DndContext>
                    )}

                    {/* Automatic */}
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6" fontWeight={700}>
                            Then, automatically
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {automatic.length < automaticTotal
                                ? `first ${automatic.length} of ${automaticTotal} products`
                                : `${automatic.length} product${automatic.length === 1 ? "" : "s"}`}
                        </Typography>
                    </Box>
                    {automatic.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            No other products here.
                        </Typography>
                    ) : (
                        <Box sx={{ ...gridSx, mb: 3 }}>
                            {automatic.map((item, i) => {
                                const pushed = isPushed(item);
                                return (
                                    <ProductCard
                                        key={item.product.product_id}
                                        item={item}
                                        position={pins.length + i + 1}
                                        salesDays={salesDays}
                                        faded={pushed}
                                        note={pushed ? "Will be pushed down" : undefined}
                                    >
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<PushPinOutlined fontSize="small" />}
                                                onClick={() => pin(item)}
                                                sx={{ flex: 1, minWidth: 0 }}
                                            >
                                                Pin
                                            </Button>
                                            <Tooltip title={pushed ? "Keep in its place" : "Push down — show it after everything else"}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => togglePush(item)}
                                                    aria-label={pushed ? "Keep in its place" : "Push down"}
                                                    color={pushed ? "primary" : "default"}
                                                >
                                                    {pushed ? <Undo fontSize="small" /> : <ArrowDownward fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </ProductCard>
                                );
                            })}
                        </Box>
                    )}

                    {/* Pushed down */}
                    {pushedList.length > 0 && (
                        <>
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, flexWrap: "wrap" }}>
                                <Typography variant="h6" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <ArrowDownward fontSize="small" color="action" /> Pushed down
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {pushedList.length} · shown after everything else, everywhere on the website
                                </Typography>
                            </Box>
                            <Box sx={{ ...gridSx, mb: 3 }}>
                                {pushedList.map((item) => {
                                    const pushed = isPushed(item);
                                    return (
                                        <ProductCard
                                            key={item.product.product_id}
                                            item={item}
                                            salesDays={salesDays}
                                            faded={!pushed}
                                            note={!pushed ? "Will return to its place" : undefined}
                                        >
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => togglePush(item)}
                                                    sx={{ flex: 1, minWidth: 0, whiteSpace: "nowrap" }}
                                                >
                                                    {pushed ? "Undo" : "Keep down"}
                                                </Button>
                                                <Tooltip title="Pin to the top">
                                                    <IconButton size="small" onClick={() => pin(item)} aria-label="Pin to the top">
                                                        <PushPinOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </ProductCard>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* Save bar */}
            {dirty && (
                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        left: { xs: 8, md: "auto" },
                        right: { xs: 8, md: 24 },
                        bottom: { xs: 8, md: 24 },
                        zIndex: (t) => t.zIndex.appBar,
                        borderRadius: 3,
                        px: 2,
                        py: 1.25,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                        Unsaved changes
                    </Typography>
                    <Button color="inherit" onClick={discard} disabled={saving}>
                        Discard
                    </Button>
                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        Save
                    </Button>
                </Paper>
            )}

            {/* Switching lists with unsaved changes */}
            <Dialog open={!!pendingScope} onClose={() => setPendingScope(null)}>
                <DialogTitle>Discard unsaved changes?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your changes to {scopeName} aren't saved yet. Switching to {pendingScope?.label} will lose them.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingScope(null)}>Stay</Button>
                    <Button
                        color="error"
                        onClick={() => {
                            const next = pendingScope;
                            setPendingScope(null);
                            if (next) {
                                setSaved("");
                                setScope(next);
                            }
                        }}
                    >
                        Discard and switch
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ArrangeShop;
